import React, { useState, useEffect } from 'react'
import { usePermissions, Permission } from '../../hooks/usePermissions'
import { getApiUrl, createFetchConfig } from '../../config/api'
import {
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'

interface AuditLog {
  id: string
  action: string
  entityType: string
  entityId?: string
  description?: string
  oldValues?: any
  newValues?: any
  ipAddress?: string
  userAgent?: string
  createdAt: string
  user: {
    id: string
    name: string
    email: string
    avatar?: string
  }
}

interface Pagination {
  page: number
  limit: number
  total: number
  pages: number
}

interface AuditLogState {
  auditLogs: AuditLog[]
  loading: boolean
  error: string | null
  pagination: Pagination
  filters: {
    startDate: string
    endDate: string
    action: string
    entityType: string
    userId: string
  }
  expandedLogs: Set<string>
  showFilters: boolean
}

const AuditLogManagement = () => {
  const { hasPermission } = usePermissions()
  
  const [state, setState] = useState<AuditLogState>({
    auditLogs: [],
    loading: true,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    },
    filters: {
      startDate: '',
      endDate: '',
      action: 'all',
      entityType: 'all',
      userId: 'all'
    },
    expandedLogs: new Set(),
    showFilters: false
  })

  const fetchAuditLogs = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      const params = new URLSearchParams({
        page: state.pagination.page.toString(),
        limit: state.pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(state.filters).filter(([_, value]) => value && value !== 'all')
        )
      })

      const response = await fetch(
        `${getApiUrl('admin/audit-logs')}?${params.toString()}`,
        createFetchConfig()
      )

      if (!response.ok) {
        throw new Error('獲取稽核日誌失敗')
      }

      const data = await response.json()
      
      setState(prev => ({
        ...prev,
        auditLogs: data.data.auditLogs,
        pagination: data.data.pagination,
        loading: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || '獲取稽核日誌失敗',
        loading: false
      }))
    }
  }

  useEffect(() => {
    if (hasPermission(Permission.VIEW_AUDIT_LOGS)) {
      fetchAuditLogs()
    }
  }, [hasPermission, state.pagination.page, state.filters])

  const handleFilterChange = (key: keyof typeof state.filters, value: string) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
      pagination: { ...prev.pagination, page: 1 } // 重設到第一頁
    }))
  }

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      pagination: { ...prev.pagination, page }
    }))
  }

  const toggleLogExpansion = (logId: string) => {
    setState(prev => {
      const newExpandedLogs = new Set(prev.expandedLogs)
      if (newExpandedLogs.has(logId)) {
        newExpandedLogs.delete(logId)
      } else {
        newExpandedLogs.add(logId)
      }
      return { ...prev, expandedLogs: newExpandedLogs }
    })
  }

  const getActionColor = (action: string) => {
    if (action.includes('CREATE')) return 'bg-green-100 text-green-800'
    if (action.includes('UPDATE') || action.includes('EDIT')) return 'bg-blue-100 text-blue-800'
    if (action.includes('DELETE') || action.includes('REMOVE')) return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getActionIcon = (action: string) => {
    if (action.includes('USER') || action.includes('ROLE')) {
      return <UserIcon className="w-4 h-4" />
    }
    return <DocumentTextIcon className="w-4 h-4" />
  }

  const formatActionName = (action: string) => {
    const actionMap: { [key: string]: string } = {
      'UPDATE_USER_ROLE': '更新使用者角色',
      'CREATE_GLOBAL_WORK_LOG': '建立全域工作日誌',
      'UPDATE_GLOBAL_WORK_LOG': '更新全域工作日誌',
      'DELETE_GLOBAL_WORK_LOG': '刪除全域工作日誌',
      'CREATE_WORK_LOG': '建立工作日誌',
      'UPDATE_WORK_LOG': '更新工作日誌',
      'DELETE_WORK_LOG': '刪除工作日誌',
    }
    return actionMap[action] || action
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // 權限檢查
  if (!hasPermission(Permission.VIEW_AUDIT_LOGS)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
          <p className="mt-1 text-sm text-gray-500">您沒有權限查看稽核日誌</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <h2 className="text-base md:text-lg font-medium text-gray-900 flex items-center">
            <ClockIcon className="w-5 md:w-6 h-5 md:h-6 mr-2 text-gray-500" />
            稽核日誌 ({state.pagination.total})
          </h2>
          <button
            onClick={() => setState(prev => ({ ...prev, showFilters: !prev.showFilters }))}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            <FunnelIcon className="w-4 h-4 mr-2" />
            篩選器
          </button>
        </div>

        {/* Filters */}
        {state.showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  開始日期
                </label>
                <input
                  type="date"
                  value={state.filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  結束日期
                </label>
                <input
                  type="date"
                  value={state.filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  操作類型
                </label>
                <select
                  value={state.filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有操作</option>
                  <option value="UPDATE_USER_ROLE">更新角色</option>
                  <option value="CREATE_GLOBAL_WORK_LOG">建立工作日誌</option>
                  <option value="UPDATE_GLOBAL_WORK_LOG">更新工作日誌</option>
                  <option value="DELETE_GLOBAL_WORK_LOG">刪除工作日誌</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  實體類型
                </label>
                <select
                  value={state.filters.entityType}
                  onChange={(e) => handleFilterChange('entityType', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有類型</option>
                  <option value="User">使用者</option>
                  <option value="WorkLog">工作日誌</option>
                  <option value="Project">專案</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setState(prev => ({
                    ...prev,
                    filters: {
                      startDate: '',
                      endDate: '',
                      action: 'all',
                      entityType: 'all',
                      userId: 'all'
                    }
                  }))}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  清除篩選
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {state.loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : state.error ? (
        <div className="px-6 py-12 text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
          <p className="mt-1 text-sm text-gray-500">{state.error}</p>
          <button
            onClick={fetchAuditLogs}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      ) : state.auditLogs.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">沒有稽核記錄</h3>
          <p className="mt-1 text-sm text-gray-500">目前沒有符合篩選條件的稽核記錄</p>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    使用者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    實體
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    描述
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    詳情
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.auditLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {formatDate(log.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {log.user.avatar ? (
                          <img
                            src={log.user.avatar}
                            alt={log.user.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {log.user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{log.user.name}</div>
                          <div className="text-sm text-gray-500">{log.user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                        <span className="ml-1">{formatActionName(log.action)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.entityType}
                    </td>
                    <td className="px-6 py-4 max-w-xs text-sm text-gray-900 truncate">
                      {log.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {(log.oldValues || log.newValues) && (
                        <button
                          onClick={() => toggleLogExpansion(log.id)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          {state.expandedLogs.has(log.id) ? (
                            <>
                              <ChevronUpIcon className="w-4 h-4 mr-1" />
                              收合
                            </>
                          ) : (
                            <>
                              <ChevronDownIcon className="w-4 h-4 mr-1" />
                              展開
                            </>
                          )}
                        </button>
                      )}
                    </td>
                    </tr>
                    {state.expandedLogs.has(log.id) && (
                      <tr>
                      <td colSpan={6} className="px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {log.oldValues && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">變更前：</h4>
                              <pre className="bg-white p-3 rounded border text-xs text-gray-700 overflow-auto">
                                {JSON.stringify(log.oldValues, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.newValues && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">變更後：</h4>
                              <pre className="bg-white p-3 rounded border text-xs text-gray-700 overflow-auto">
                                {JSON.stringify(log.newValues, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.ipAddress && (
                            <div className="md:col-span-2">
                              <span className="text-gray-500">IP 位址: {log.ipAddress}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            {state.auditLogs.map((log) => (
              <div key={log.id} className="border-b border-gray-200 p-4">
                <div className="flex items-start space-x-3">
                  {log.user.avatar ? (
                    <img
                      src={log.user.avatar}
                      alt={log.user.name}
                      className="w-10 h-10 rounded-full flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-gray-600">
                        {log.user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{log.user.name}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                        <span className="ml-1">{formatActionName(log.action)}</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{log.user.email}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(log.createdAt)}</p>
                    {log.description && (
                      <p className="text-sm text-gray-700 mt-2">{log.description}</p>
                    )}
                    {(log.oldValues || log.newValues) && (
                      <button
                        onClick={() => toggleLogExpansion(log.id)}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-sm flex items-center"
                      >
                        {state.expandedLogs.has(log.id) ? (
                          <>
                            <ChevronUpIcon className="w-4 h-4 mr-1" />
                            收合詳情
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-4 h-4 mr-1" />
                            查看詳情
                          </>
                        )}
                      </button>
                    )}
                    {state.expandedLogs.has(log.id) && (
                      <div className="mt-3 space-y-3">
                        {log.oldValues && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">變更前：</h4>
                            <pre className="bg-gray-100 p-2 rounded text-xs text-gray-700 overflow-auto">
                              {JSON.stringify(log.oldValues, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.newValues && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">變更後：</h4>
                            <pre className="bg-gray-100 p-2 rounded text-xs text-gray-700 overflow-auto">
                              {JSON.stringify(log.newValues, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {state.pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  顯示第 {(state.pagination.page - 1) * state.pagination.limit + 1} 到{' '}
                  {Math.min(state.pagination.page * state.pagination.limit, state.pagination.total)} 筆，
                  共 {state.pagination.total} 筆
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(state.pagination.page - 1)}
                    disabled={state.pagination.page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    上一頁
                  </button>
                  <span className="px-3 py-2 text-sm font-medium text-gray-900">
                    {state.pagination.page} / {state.pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(state.pagination.page + 1)}
                    disabled={state.pagination.page === state.pagination.pages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    下一頁
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AuditLogManagement